import { billService } from "../billService";
import { transactionService } from "../transactionService";
import { normalizeAccountType } from "../coaService";

const VOID_STATUSES = new Set(["void", "voided", "cancelled", "canceled"]);
const EXCLUDED_EXPENSE_STATUSES = new Set(["void", "voided", "deleted", "reversed"]);

function requireOrganization(organizationId) {
  if (!organizationId) {
    throw new Error("Select a business to view its bills and expenses.");
  }
}

function normalizedStatus(bill) {
  const status = String(bill?.status || "unknown").trim().toLowerCase();
  const dueDate = String(bill?.due_date || "").slice(0, 10);
  const balance = Number(bill?.balance_due);

  if (
    ["unpaid", "partially_paid"].includes(status) &&
    Number.isFinite(balance) &&
    balance > 0 &&
    dueDate &&
    dueDate < new Date().toISOString().slice(0, 10)
  ) {
    return "overdue";
  }

  return status;
}

function vendorName(vendor) {
  return vendor?.vendor_name || vendor?.company_name || vendor?.name || "";
}

function mapBill(bill, vendors) {
  const vendor = vendors.find((row) => row.id === bill.vendor_id) || null;
  return { ...bill, vendor, displayStatus: normalizedStatus(bill) };
}

function amountByCurrency(rows, getAmount) {
  return rows.reduce((totals, row) => {
    const amount = Number(getAmount(row));
    if (!Number.isFinite(amount)) return totals;
    const currency = String(row.currency || "USD").toUpperCase();
    totals[currency] = (totals[currency] || 0) + amount;
    return totals;
  }, {});
}

function expenseLine(row) {
  const accountType = normalizeAccountType(row.account?.type);
  const status = String(row.status || "").toLowerCase();
  return (
    accountType === "Expense" &&
    !EXCLUDED_EXPENSE_STATUSES.has(status) &&
    Number(row.debit) > 0
  );
}

export const clientBillService = {
  async getClientBills({ organizationId }) {
    requireOrganization(organizationId);
    const [bills, vendors] = await Promise.all([
      billService.getBills(organizationId),
      billService.getVendors(organizationId),
    ]);

    return {
      bills: (bills || []).map((bill) => mapBill(bill, vendors || [])),
      vendors: vendors || [],
    };
  },

  async getClientBillDetails({ organizationId, billId }) {
    requireOrganization(organizationId);
    if (!billId) throw new Error("Bill ID is required.");

    const [bill, vendors, payments] = await Promise.all([
      billService.getBillById(organizationId, billId),
      billService.getVendors(organizationId),
      billService.getBillPayments(organizationId, billId),
    ]);

    if (bill.organization_id !== organizationId) {
      throw new Error("This bill is not available in the selected business.");
    }

    return {
      ...mapBill(bill, vendors || []),
      payments: payments || [],
    };
  },

  async getClientExpenses({ organizationId }) {
    requireOrganization(organizationId);
    const transactions = await transactionService.getTransactions(
      {},
      organizationId,
    );
    return (transactions || [])
      .filter(expenseLine)
      .map((transaction) => ({
        ...transaction,
        vendorName:
          transaction.payee ||
          transaction.vendor?.vendor_name ||
          transaction.vendor?.company_name ||
          "",
        amount: Number(transaction.debit),
        currency:
          transaction.account?.currency ||
          transaction.source_account?.currency ||
          "USD",
      }));
  },

  async getClientExpenseDetails({ organizationId, expenseId }) {
    requireOrganization(organizationId);
    if (!expenseId) throw new Error("Expense ID is required.");

    const transaction = await transactionService.getTransactionById(
      expenseId,
      organizationId,
    );

    if (!transaction || !expenseLine(transaction)) {
      throw new Error("This expense is not available in the selected business.");
    }

    return {
      ...transaction,
      amount: Number(transaction.debit),
      currency:
        transaction.account?.currency ||
        transaction.source_account?.currency ||
        "USD",
    };
  },
};

export { amountByCurrency, normalizedStatus, vendorName };